import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { useAuth } from "../context/AuthContext";

const AddManualCustomer = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    dob: "",
    policyId: "",
    contact: "",
    email: "",
    address: "",
    company: "",
    category: "",
    type: "",
    premium: "",
    paymentMode: "Monthly",
    startDate: "",
    maturityDate: "",
    nomineeName: "",
    nomineeRelation: "",
    branchName: "",
  });

  // NEW: validation errors map
  const [errors, setErrors] = useState({});

  const insuranceOptions = {
    "Life Insurance": [
      "Term Insurance ",
      "Whole Life Insurance",
      "Endowment Insurance",
      "Money-Back Insurance",
      "ULIP Insurance",
      "Child Insurance",
      "Pension Insurance",
    ],
    "General Insurance": [
      "Health Insurance",
      "Motor Insurance",
      "Travel Insurance",
      "Home Insurance",
      "Fire Insurance",
      "Marine Insurance",
      "Personal Accident Insurance",
      "Commercial Insurance",
    ],
  };

  const insuranceCompanies = [
    "LIC",
    "HDFC Life",
    "SBI Life",
    "ICICI Prudential Life",
    "Max Life",
    "New India Assurance",
    "ICICI Lombard",
    "Bajaj Allianz General",
    "Tata AIG",
    "HDFC ERGO",
  ];

  const toggleSidebar = () => setSidebarOpen((s) => !s);

  // ---------- VALIDATION HELPERS ----------
  const isFuture = (d) => new Date(d) > new Date();
  const ageFromDob = (d) => {
    if (!d) return 0;
    const dob = new Date(d);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
    return age;
  };

  const validators = {
    name: (v) =>
      !v.trim()
        ? "Customer name is required"
        : !/^[A-Za-z][A-Za-z\s'.-]{2,}$/.test(v)
        ? "Enter a valid name (min 3 letters)"
        : "",

    dob: (v) =>
      !v
        ? "Date of birth is required"
        : isFuture(v)
        ? "DOB cannot be in the future"
        : "",

    contact: (v) =>
      !v
        ? "Contact number is required"
        : !/^(?:\[\-\s]?|0)?[6-9]\d{9}$/.test(v)
        ? "Enter a valid Indian mobile number"
        : "",

    email: (v) =>
      !v
        ? "Email is required"
        : !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v)
        ? "Enter a valid email address"
        : "",

    address: (v) =>
      !v.trim()
        ? "Address is required"
        : v.trim().length < 5
        ? "Address seems too short"
        : "",

    company: (v) => (!v ? "Select an insurance company" : ""),

    policyId: (v) =>
      !v
        ? "Policy number is required"
        : !/^[A-Za-z0-9\-\/]{6,25}$/.test(v)
        ? "Policy number must be 6–25 chars (letters, numbers, - /)"
        : "",

    category: (v) => (!v ? "Select a category" : ""),
    type: (v, all) => (!all.category ? "" : !v ? "Select a type" : ""),

    premium: (v) =>
      v === "" || v === null
        ? "Premium is required"
        : Number(v) <= 0
        ? "Premium must be greater than 0"
        : "",

    paymentMode: (v) => (!v ? "Select payment mode" : ""),

    startDate: (v) => (!v ? "Premium start date is required" : ""),


    maturityDate: (v, all) =>
      !v
        ? "Maturity date is required"
        : all.startDate && new Date(v) <= new Date(all.startDate)
        ? "Maturity date must be after start date"
        : "",

    nomineeName: (v) =>
      !v
        ? "Nominee name is required"
        : !/^[A-Za-z][A-Za-z\s'.-]{2,}$/.test(v)
        ? "Enter a valid nominee name"
        : "",

    nomineeRelation: (v) =>
      !v
        ? "Nominee relation is required"
        : !/^[A-Za-z\s'.-]{3,}$/.test(v)
        ? "Enter a valid relation"
        : "",

    branchName: (v) =>
      !v.trim()
        ? "Branch name is required"
        : v.trim().length < 2
        ? "Branch name seems too short"
        : "",
  };

  const validateField = (name, value, all = formData) => {
    const fn = validators[name];
    if (!fn) return "";
    return fn(value, all);
  };

  const validateAll = (all = formData) => {
    const nextErrors = {};
    Object.keys(validators).forEach((key) => {
      const msg = validators[key](all[key], all);
      if (msg) nextErrors[key] = msg;
    });
    return nextErrors;
  };
  // ----------------------------------------

  const handleChange = (e) => {
    const { name, value } = e.target;
    const next = { ...formData, [name]: value };

    // Keep your category -> type reset behavior
    if (name === "category") next.type = "";

    setFormData(next);

    // live-validate changed field
    const msg = validateField(name, next[name], next);
    setErrors((prev) => ({ ...prev, [name]: msg }));

    // if maturity/start dependency, revalidate both
    if (name === "startDate" && next.maturityDate) {
      const mMsg = validateField("maturityDate", next.maturityDate, next);
      setErrors((prev) => ({ ...prev, maturityDate: mMsg }));
    }
    if (name === "maturityDate" && next.startDate) {
      const sMsg = validateField("startDate", next.startDate, next);
      setErrors((prev) => ({ ...prev, startDate: sMsg }));
    }
    if (name === "category") {
      const tMsg = validateField("type", next.type, next);
      setErrors((prev) => ({ ...prev, type: tMsg }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    const msg = validateField(name, formData[name]);
    setErrors((prev) => ({ ...prev, [name]: msg }));
  };

  const getNextDueDate = (startDate, paymentMode) => {
    const date = new Date(startDate);
    if (paymentMode === "Monthly") date.setMonth(date.getMonth() + 1);
    else if (paymentMode === "Quarterly") date.setMonth(date.getMonth() + 3);
    else if (paymentMode === "Half-Yearly") date.setMonth(date.getMonth() + 6);
    else if (paymentMode === "Yearly") date.setFullYear(date.getFullYear() + 1);
    return date.toISOString().split("T")[0];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const allErrors = validateAll(formData);
    setErrors(allErrors);
    const firstErrorKey = Object.keys(allErrors)[0];
    if (firstErrorKey) {
      const el = document.querySelector(`[name="${firstErrorKey}"]`);
      if (el && typeof el.focus === "function") el.focus();
      alert("Please fix the highlighted fields.");
      return;
    }

    const agent = JSON.parse(localStorage.getItem("user"));
    if (!agent || !agent.id) {
      alert("Agent not logged in. Please log in again.");
      return;
    }

    const dueDate = getNextDueDate(formData.startDate, formData.paymentMode);

    const payload = {
      agentId: agent.id,
      agentCode: "manual123",
      name: formData.name,
      dob: formData.dob,
      address: formData.address,
      contact: formData.contact,
      email: formData.email,
      company: formData.company,
      policies: [
        {
          policyId: formData.policyId,
          category: formData.category,
          policyType: formData.type,
          premium: Number(formData.premium),
          paymentMode: formData.paymentMode,
          startDate: formData.startDate,
          maturityDate: formData.maturityDate,
          nomineeName: formData.nomineeName,
          nomineeRelation: formData.nomineeRelation,
          branchName: formData.branchName,
          dueDate,
        },
      ],
    };

    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/api/agent/manual-policy`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const data = await res.json();

      if (res.ok) {
        alert("✅ Policy saved.");
        navigate("/agent/companies");
      } else {
        alert("❌ " + (data.msg || "Something went wrong"));
      }
    } catch (err) {
      console.error("Manual policy error:", err);
      alert("❌ Failed to submit policy.");
    }
  };

  // Right panel content
  const whyItems = [
    { icon: "bi-shield-check", title: "Comprehensive Coverage", bg: "rgba(59,130,246,.12)", color: "#2563eb" },
    { icon: "bi-speedometer2", title: "Quick Claim Processing", bg: "rgba(34,197,94,.12)", color: "#16a34a" },
    { icon: "bi-people", title: "Trusted by Millions", bg: "rgba(99,102,241,.12)", color: "#4f46e5" },
    { icon: "bi-headset", title: "Expert Support Team", bg: "rgba(234,88,12,.12)", color: "#ea580c" },
    { icon: "bi-heart-pulse", title: "Customer First Approach", bg: "rgba(2,132,199,.12)", color: "#0284c7" },
  ];

  const invalidCls = (name) =>
    `form-control ${errors[name] ? "is-invalid" : ""}`;
  const invalidSel = (name) =>
    `form-select ${errors[name] ? "is-invalid" : ""}`;

  return (
    <div className="dashboard-layout d-flex">
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      {sidebarOpen && <div className="overlay" onClick={toggleSidebar} />}

      <div className="main-content flex-grow-1">
        <div className="container py-4">
          {/* Page header */}
          <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-3">
            <div className="d-flex align-items-center gap-3">
              <div
                className="rounded-circle bg-primary-subtle text-primary d-inline-flex align-items-center justify-content-center"
                style={{ width: 42, height: 42 }}
              >
                <i className="bi bi-file-earmark-plus" />
              </div>
              <div>
                <h4 className="fw-bold mb-0">Add New Policy</h4>
                <small className="text-muted">
                  Fill in the customer and insurance details
                </small>
              </div>
            </div>
          </div>

          {/* Two-column layout */}
          <div className="row g-3">
            {/* LEFT: form */}
            <div className="col-lg-8 col-xl-9">
              <form onSubmit={handleSubmit} className="form-shell card border-0 shadow-sm rounded-4">
                <div className="card-body p-4 p-md-5">

                  {/* Customer Information */}
                  <div className="form-section">
                    <h5 className="form-section__title">Customer Information</h5>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label">
                          Customer Name <span className="text-danger">*</span>
                        </label>
                        <div className="input-group input-group-lg icon-input">
                          <span className="input-group-text"><i className="bi bi-person" /></span>
                          <input
                            type="text"
                            name="name"
                            className={invalidCls("name")}
                            placeholder="Enter customer name"
                            value={formData.name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required
                          />
                          {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                        </div>
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">
                          Date of Birth <span className="text-danger">*</span>
                        </label>
                        <div className="input-group input-group-lg icon-input">
                          <span className="input-group-text"><i className="bi bi-calendar-event" /></span>
                          <input
                            type="date"
                            name="dob"
                            className={invalidCls("dob")}
                            value={formData.dob}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required
                            max={new Date().toISOString().split("T")[0]}
                          />
                          {errors.dob && <div className="invalid-feedback">{errors.dob}</div>}
                        </div>
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">
                          Contact Number <span className="text-danger">*</span>
                        </label>
                        <div className="input-group input-group-lg icon-input">
                          <span className="input-group-text"><i className="bi bi-telephone" /></span>
                          <input
                            type="tel"
                            name="contact"
                            className={invalidCls("contact")}
                            placeholder="e.g. 98XXXXXXXX or +91 98XXXXXXXX"
                            value={formData.contact}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required
                          />
                          {errors.contact && <div className="invalid-feedback">{errors.contact}</div>}
                        </div>
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">
                          Email Address <span className="text-danger">*</span>
                        </label>
                        <div className="input-group input-group-lg icon-input">
                          <span className="input-group-text"><i className="bi bi-envelope" /></span>
                          <input
                            type="email"
                            name="email"
                            className={invalidCls("email")}
                            placeholder="Enter email address"
                            value={formData.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required
                          />
                          {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                        </div>
                      </div>

                      <div className="col-12">
                        <label className="form-label">
                          Address <span className="text-danger">*</span>
                        </label>
                        <div className="input-group input-group-lg icon-input">
                          <span className="input-group-text"><i className="bi bi-geo-alt" /></span>
                          <input
                            type="text"
                            name="address"
                            className={invalidCls("address")}
                            placeholder="House / Street / City"
                            value={formData.address}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required
                          />
                          {errors.address && <div className="invalid-feedback">{errors.address}</div>}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Policy Details */}
                  <div className="form-section">
                    <h5 className="form-section__title">Policy Details</h5>
                    <div className="row g-2">
                      <div className="col-md-6">
                        <label className="form-label">
                          Insurance Company <span className="text-danger">*</span>
                        </label>
                        <div className="input-group input-group-lg icon-input">
                          <span className="input-group-text"><i className="bi bi-building" /></span>
                          <select
                            name="company"
                            className={invalidSel("company")}
                            value={formData.company}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required
                          >
                            <option value="">Select insurance company</option>
                            {insuranceCompanies.map((c, i) => (
                              <option key={i} value={c}>{c}</option>
                            ))}
                          </select>
                          {errors.company && <div className="invalid-feedback">{errors.company}</div>}
                        </div>
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">
                          Policy Number <span className="text-danger">*</span>
                        </label>
                        <div className="input-group input-group-lg icon-input">
                          <span className="input-group-text"><i className="bi bi-upc-scan" /></span>
                          <input
                            type="text"
                            name="policyId"
                            className={invalidCls("policyId")}
                            placeholder="Enter policy number"
                            value={formData.policyId}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required
                          />
                          {errors.policyId && <div className="invalid-feedback">{errors.policyId}</div>}
                        </div>
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">
                          Insurance Category <span className="text-danger">*</span>
                        </label>
                        <div className="input-group input-group-lg icon-input">
                          <span className="input-group-text"><i className="bi bi-diagram-3" /></span>
                          <select
                            name="category"
                            className={invalidSel("category")}
                            value={formData.category}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required
                          >
                            <option value="">Select category</option>
                            {Object.keys(insuranceOptions).map((cat, i) => (
                              <option key={i} value={cat}>{cat}</option>
                            ))}
                          </select>
                          {errors.category && <div className="invalid-feedback">{errors.category}</div>}
                        </div>
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">
                          Insurance Type <span className="text-danger">*</span>
                        </label>
                        <div className="input-group input-group-lg icon-input">
                          <span className="input-group-text"><i className="bi bi-card-checklist" /></span>
                          <select
                            name="type"
                            className={invalidSel("type")}
                            value={formData.type}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={!formData.category}
                            required
                          >
                            <option value="">Select type</option>
                            {formData.category &&
                              insuranceOptions[formData.category].map((t, idx) => (
                                <option key={idx} value={t}>{t}</option>
                              ))}
                          </select>
                          {errors.type && <div className="invalid-feedback">{errors.type}</div>}
                        </div>
                      </div>

                      <div className="col-12">
                        <label className="form-label">
                          Branch Name <span className="text-danger">*</span>
                        </label>
                        <div className="input-group input-group-lg icon-input">
                          <span className="input-group-text"><i className="bi bi-geo-alt" /></span>
                          <input
                            type="text"
                            name="branchName"
                            className={invalidCls("branchName")}
                            placeholder="Enter branch name"
                            value={formData.branchName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required
                          />
                          {errors.branchName && <div className="invalid-feedback">{errors.branchName}</div>}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Premium Information */}
                  <div className="form-section">
                    <h5 className="form-section__title">Premium Information</h5>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label">
                          Premium Amount <span className="text-danger">*</span>
                        </label>
                        <div className="input-group input-group-lg icon-input">
                          <span className="input-group-text"><i className="bi bi-currency-rupee" /></span>
                          <input
                            type="number"
                            name="premium"
                            className={invalidCls("premium")}
                            placeholder="Enter premium amount"
                            value={formData.premium}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required
                            min="1"
                            step="0.01"
                          />
                          {errors.premium && <div className="invalid-feedback">{errors.premium}</div>}
                        </div>
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">
                          Payment Mode <span className="text-danger">*</span>
                        </label>
                        <div className="input-group input-group-lg icon-input">
                          <span className="input-group-text"><i className="bi bi-cash-coin" /></span>
                          <select
                            name="paymentMode"
                            className={invalidSel("paymentMode")}
                            value={formData.paymentMode}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required
                          >
                            <option value="Monthly">Monthly</option>
                            <option value="Quarterly">Quarterly</option>
                            <option value="Half-Yearly">Half-Yearly</option>
                            <option value="Yearly">Yearly</option>
                          </select>
                          {errors.paymentMode && <div className="invalid-feedback">{errors.paymentMode}</div>}
                        </div>
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">
                          Premium Start Date <span className="text-danger">*</span>
                        </label>
                        <div className="input-group input-group-lg icon-input">
                          <span className="input-group-text"><i className="bi bi-calendar2-week" /></span>
                          <input
                            type="date"
                            name="startDate"
                            className={invalidCls("startDate")}
                            value={formData.startDate}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required
                          />
                          {errors.startDate && <div className="invalid-feedback">{errors.startDate}</div>}
                        </div>
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">
                          Maturity Date <span className="text-danger">*</span>
                        </label>
                        <div className="input-group input-group-lg icon-input">
                          <span className="input-group-text"><i className="bi bi-calendar-check" /></span>
                          <input
                            type="date"
                            name="maturityDate"
                            className={invalidCls("maturityDate")}
                            value={formData.maturityDate}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required
                          />
                          {errors.maturityDate && <div className="invalid-feedback">{errors.maturityDate}</div>}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Nominee Details */}
                  <div className="form-section">
                    <h5 className="form-section__title">Nominee Details</h5>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label">
                          Nominee Name <span className="text-danger">*</span>
                        </label>
                        <div className="input-group input-group-lg icon-input">
                          <span className="input-group-text"><i className="bi bi-person-badge" /></span>
                          <input
                            type="text"
                            name="nomineeName"
                            className={invalidCls("nomineeName")}
                            placeholder="Enter nominee name"
                            value={formData.nomineeName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required
                          />
                          {errors.nomineeName && <div className="invalid-feedback">{errors.nomineeName}</div>}
                        </div>
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">
                          Nominee Relation <span className="text-danger">*</span>
                        </label>
                        <div className="input-group input-group-lg icon-input">
                          <span className="input-group-text"><i className="bi bi-people" /></span>
                          <input
                            type="text"
                            name="nomineeRelation"
                            className={invalidCls("nomineeRelation")}
                            placeholder="Enter relation"
                            value={formData.nomineeRelation}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required
                          />
                          {errors.nomineeRelation && <div className="invalid-feedback">{errors.nomineeRelation}</div>}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Submit */}
                  <div className="d-flex justify-content-start pt-3">
                    <button type="submit" className="btn blue__btn">
                      Save Insurance Policy
                    </button>
                  </div>

                </div>
              </form>
            </div>

            {/* RIGHT: Why Choose Us panel */}
            <div className="col-lg-4 col-xl-3">
              <aside className="wcu card border-0 shadow-sm rounded-4">
                <div className="card-body p-4">
                  <h5 className="fw-bold mb-1">Our Services</h5>
                  <div className="text-muted small mb-3">Trusted by millions</div>
                  <ul className="wcu__list">
                    {whyItems.map((it, i) => (
                      <li key={i} className="wcu__item d-flex align-items-start gap-3">
                        <div className="wcu__icon" style={{ background: it.bg, color: it.color }} aria-hidden="true">
                          <i className={`bi ${it.icon}`} />
                        </div>
                        <div className="wcu__text">
                          <div className="wcu__title">{it.title}</div>
                          <div className="wcu__desc">{it.desc}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddManualCustomer;
